import {
  Alert,
  Button,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  SegmentedControl,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import { DateInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { Decimal } from 'decimal.js';
import { useState } from 'react';
import type { ReactNode } from 'react';
import { z } from 'zod';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { translating } from '../i18n/resolver.js';
import { useT } from '../i18n/useT.js';
import { formatDate, formatMoney, toIsoDate } from '../format.js';
import {
  useAccounts,
  useBankTransaction,
  useJournalEntries,
  useReconcile,
  useUnreconcile,
} from '../api/hooks.js';
import type { BankTransaction } from '../api/hooks.js';

/**
 * Matching one bank transaction to the ledger.
 *
 * **Matching does not decide whether to book the transaction. It decides what
 * the entry should be** — and that depends on whether the event was already
 * booked. The two answers are genuinely different postings, so they are two
 * explicit choices here rather than one clever form that guesses.
 *
 * Getting it wrong is not cosmetic. An incoming payment against an invoice that
 * is already booked has to settle the debtor; booking it fresh against a
 * revenue account instead credits revenue a second time for one sale, and pays
 * VAT on it twice.
 *
 * One transaction may need several entries — a Leverandørservice collection
 * settles per creditor per run — so the modal stays open on success and shows
 * the remaining difference.
 */

export interface BankMatchModalProps {
  readonly transaction: BankTransaction | null;
  readonly opened: boolean;
  readonly onClose: () => void;
}

export function BankMatchModal({
  transaction,
  opened,
  onClose,
}: BankMatchModalProps): ReactNode {
  const t = useT();
  const detail = useBankTransaction(transaction === null ? null : transaction.id);
  const unreconcile = useUnreconcile();
  const [mode, setMode] = useState<string>('existing');

  const remaining = detail.data === undefined ? null : new Decimal(detail.data.difference);
  const settled = remaining !== null && remaining.isZero();

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      size="lg"
      title={
        transaction === null
          ? ''
          : t('match.title', {
              date: formatDate(transaction.date),
              amount: formatMoney(transaction.amount),
            })
      }
    >
      <Stack gap="md">
        {transaction !== null && (
          <Text size="sm" c="dimmed">
            {transaction.description ?? t('match.noBankText')}
          </Text>
        )}

        {detail.isPending && (
          <Center p="md">
            <Loader />
          </Center>
        )}

        {detail.isError && <ErrorMessage error={detail.error} />}

        {detail.isSuccess && (
          <>
            <Group justify="space-between">
              <Text size="sm">{t('match.postedSoFar')}</Text>
              <Text className="money-cell">{formatMoney(detail.data.matchedAmount)}</Text>
            </Group>
            <Group justify="space-between">
              <Text size="sm" fw={600}>
                {t('match.difference')}
              </Text>
              <Text className="money-cell" fw={600} c={settled ? 'green' : 'orange'}>
                {formatMoney(detail.data.difference)}
              </Text>
            </Group>

            {detail.data.reconciliations.length > 0 && (
              <>
                <Divider label={t('match.postedEntries')} labelPosition="left" />
                <Table>
                  <Table.Tbody>
                    {detail.data.reconciliations.map((match) => (
                      <Table.Tr key={match.id}>
                        <Table.Td>
                          <Text size="sm">{formatDate(match.entryDate)}</Text>
                        </Table.Td>
                        <Table.Td>
                          <Text size="sm">{match.entryDescription}</Text>
                        </Table.Td>
                        <Table.Td style={{ width: 130 }}>
                          <Button
                            size="compact-sm"
                            variant="subtle"
                            color="red"
                            onClick={() => {
                              unreconcile.mutate({
                                id: match.bankTransactionId,
                                entryId: match.entryId,
                              });
                            }}
                            loading={unreconcile.isPending}
                          >
                            {t('match.removeMatch')}
                          </Button>
                        </Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
                {/*
                  Said where the button is, because "fjern match" reads like an
                  undo and only half of it is one.
                */}
                <Text size="xs" c="dimmed">
                  {t('match.removeNote')}
                </Text>
                {unreconcile.isError && (
                  <ErrorMessage
                    error={unreconcile.error}
                    title={t('match.removeFailed')}
                  />
                )}
              </>
            )}

            <Divider label={t('match.reconcile')} labelPosition="left" />

            <SegmentedControl
              value={mode}
              onChange={setMode}
              aria-label={t('match.modeLabel')}
              data={[
                { value: 'existing', label: t('match.mode.existing') },
                { value: 'fresh', label: t('match.mode.fresh') },
              ]}
            />

            {mode === 'existing' ? (
              <MatchExistingEntryForm transactionId={detail.data.id} />
            ) : (
              <BookFreshForm
                transactionId={detail.data.id}
                transactionDate={detail.data.date}
                transactionDescription={detail.data.description}
              />
            )}
          </>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={onClose}>
            {t('match.close')}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

// -----------------------------------------------------------------------------
// The event was already booked
// -----------------------------------------------------------------------------

const existingSchema = z.object({
  entryId: z.uuid('match.entryRequired'),
});

type ExistingValues = z.infer<typeof existingSchema>;

function MatchExistingEntryForm({
  transactionId,
}: {
  readonly transactionId: string;
}): ReactNode {
  const t = useT();
  const entries = useJournalEntries();
  const reconcile = useReconcile();

  const form = useForm<ExistingValues>({
    mode: 'uncontrolled',
    initialValues: { entryId: '' },
    validate: translating(existingSchema, t, 'match'),
  });

  function handleSubmit(values: ExistingValues): void {
    reconcile.mutate(
      { id: transactionId, command: { entryId: values.entryId } },
      {
        onSuccess: (result) => {
          notifications.show({
            color: 'green',
            title: t('match.reconciled'),
            message:
              result.status === 'matched'
                ? t('match.fullyReconciled')
                : t('match.stillMissing', {
                    amount: formatMoney(result.difference),
                  }),
          });
          form.reset();
        },
      },
    );
  }

  return (
    <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="sm">
        <Text size="sm" c="dimmed">
          {t('match.existingHelp')}
        </Text>

        {entries.isError && <ErrorMessage error={entries.error} />}

        <Select
          label={t('match.entry')}
          placeholder={t('match.entryPlaceholder')}
          searchable
          disabled={entries.isPending}
          data={(entries.data?.items ?? []).map((entry) => ({
            value: entry.id,
            label: `${formatDate(entry.date)} — ${entry.description}`,
          }))}
          key={form.key('entryId')}
          {...form.getInputProps('entryId')}
        />

        {reconcile.isError && (
          <ErrorMessage error={reconcile.error} title={t('match.reconcileFailed')} />
        )}

        <Group justify="flex-end">
          <Button type="submit" loading={reconcile.isPending}>
            {t('match.link')}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}

// -----------------------------------------------------------------------------
// The line was never booked
// -----------------------------------------------------------------------------

const freshSchema = z.object({
  contraAccountCode: z.string().min(1, 'match.contraAccountRequired'),
  description: z.string().min(1, 'match.textRequired'),
  date: z.date({ message: 'match.entryDateRequired' }),
});

type FreshValues = z.infer<typeof freshSchema>;

function BookFreshForm({
  transactionId,
  transactionDate,
  transactionDescription,
}: {
  readonly transactionId: string;
  readonly transactionDate: string;
  readonly transactionDescription: string | null;
}): ReactNode {
  const t = useT();
  const accounts = useAccounts();
  const reconcile = useReconcile();

  const form = useForm<FreshValues>({
    mode: 'uncontrolled',
    initialValues: {
      contraAccountCode: '',
      // Prefilled from the bank line rather than left blank. The service would
      // default to the same values, but a field showing what will be posted is
      // correctable and a silent default is not.
      description: transactionDescription ?? '',
      date: new Date(`${transactionDate}T00:00:00`),
    },
    validate: translating(freshSchema, t, 'match'),
  });

  function handleSubmit(values: FreshValues): void {
    reconcile.mutate(
      {
        id: transactionId,
        command: {
          contraAccountCode: values.contraAccountCode,
          description: values.description.trim(),
          date: toIsoDate(values.date),
        },
      },
      {
        onSuccess: (result) => {
          notifications.show({
            color: 'green',
            title: t('match.posted'),
            message:
              result.status === 'matched'
                ? t('match.fullyReconciled')
                : t('match.stillMissing', {
                    amount: formatMoney(result.difference),
                  }),
          });
          form.reset();
        },
      },
    );
  }

  return (
    <form noValidate onSubmit={form.onSubmit(handleSubmit)}>
      <Stack gap="sm">
        <Alert color="blue" variant="light" title={t('match.freshWarningTitle')}>
          {t('match.freshWarning')}
        </Alert>

        {accounts.isError && <ErrorMessage error={accounts.error} />}

        <Select
          label={t('match.contraAccount')}
          placeholder={t('match.contraAccountPlaceholder')}
          searchable
          disabled={accounts.isPending}
          data={(accounts.data?.items ?? [])
            // Only active accounts. A deactivated one keeps its history and
            // must not be postable to.
            .filter((account) => account.isActive)
            .map((account) => ({
              value: account.code,
              label: `${account.code} — ${account.name}`,
            }))}
          key={form.key('contraAccountCode')}
          {...form.getInputProps('contraAccountCode')}
        />

        <TextInput
          label={t('match.text')}
          key={form.key('description')}
          {...form.getInputProps('description')}
        />

        <DateInput
          label={t('match.entryDate')}
          valueFormat="DD.MM.YYYY"
          key={form.key('date')}
          {...form.getInputProps('date')}
        />

        {reconcile.isError && (
          <ErrorMessage error={reconcile.error} title={t('match.postFailed')} />
        )}

        <Group justify="flex-end">
          <Button type="submit" loading={reconcile.isPending}>
            {t('match.postAndReconcile')}
          </Button>
        </Group>
      </Stack>
    </form>
  );
}
